import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [showEmail, setShowEmail] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/42a03ac1b9b93d452ce0072640933a9d.jpg"
            alt="CDG Group Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#001F3F]/40 via-[#001F3F]/30 to-[#001F3F]/40"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col">
          {/* Logo at Top */}
          <div className="w-full pt-[80px] pb-[60px] max-md:pt-[40px] max-md:pb-[40px]">
            <div className="max-w-[1200px] mx-auto px-[80px] max-md:px-[24px]">
              <img
                src="https://static.readdy.ai/image/252f02ba3944c7db703aee568cd82e59/bb693eadee0c4a5cb50492cf8fb740f5.jpeg"
                alt="CDG Group Logo"
                className="h-24 w-auto max-md:h-16"
              />
            </div>
          </div>

          {/* Main Content - Centered */}
          <div className="flex-1 flex items-center">
            <div className="w-full max-w-[1200px] mx-auto px-[80px] max-md:px-[24px]">
              <div className="w-[60%] max-md:w-full">
                <h1 className="text-[72px] font-bold text-white leading-[1.2] mb-10 animate-fade-in max-md:text-[42px] max-md:mb-6">
                  Expertise in Business<br />Consulting and M&A
                </h1>
                <p className="text-[22px] text-white/85 leading-[1.6] max-w-[600px] mb-14 max-md:text-[18px] max-md:mb-10 max-md:max-w-full">
                  Transforming opportunities into results through specialized consulting in mergers, acquisitions and sales strategies
                </p>
                <button
                  onClick={() => scrollToSection('services')}
                  className="px-[72px] py-[14px] border-2 border-white bg-transparent text-white text-[16px] rounded-full hover:bg-white hover:text-[#001F3F] transition-all duration-300 whitespace-nowrap cursor-pointer max-md:px-[48px] max-md:py-[12px] max-md:text-[15px]"
                >
                  Discover Our Services
                </button>
              </div>
            </div>
          </div>

          {/* Scroll Indicator at Bottom */}
          <div className="pb-10 flex justify-center max-md:pb-6">
            <div className="animate-bounce">
              <i className="ri-arrow-down-line text-white/30 text-3xl max-md:text-2xl"></i>
            </div>
          </div>
        </div>
      </section>

      {/* Separator Line 1 */}
      <div className="w-full h-[2px] bg-gradient-to-r from-transparent via-[#007BFF]/40 to-transparent"></div>

      {/* About Section */}
      <section id="about" className="py-[140px] bg-[#FAFBFC] relative overflow-hidden max-md:py-[80px]">
        {/* Decorative Lines */}
        <div className="absolute top-[50%] left-[10%] w-[200px] h-[1px] bg-gradient-to-r from-[#007BFF]/10 to-transparent"></div>
        <div className="absolute bottom-[30%] right-[15%] w-[150px] h-[1px] bg-gradient-to-l from-[#007BFF]/10 to-transparent"></div>
        
        <div className="max-w-[1200px] mx-auto px-[80px] relative z-10 max-md:px-[24px]">
          <div className="text-center mb-10 max-md:mb-8">
            <p className="text-[13px] font-medium text-[#007BFF] tracking-[2px] uppercase mb-6 max-md:text-[12px] max-md:mb-4">
              Who We Are
            </p>
            <h2 className="text-[48px] font-semibold text-[#2C2C2C] font-serif max-md:text-[36px]">
              CDG Group
            </h2>
          </div>

          <div className="flex gap-[4%] mt-10 max-md:flex-col max-md:gap-6 max-md:mt-8">
            <div className="w-[48%] max-md:w-full">
              <p className="text-[18px] text-[#555555] leading-[1.8] text-justify max-md:text-[16px] max-md:text-left">
                CDG Group is a reference in business consulting, guiding organizations through complex merger and acquisition processes. Our personalized approach combines deep market analysis with innovative strategies.
              </p>
            </div>
            <div className="relative w-[48%] max-md:w-full">
              <div className="absolute left-[-2%] top-1/2 transform -translate-y-1/2 w-[1px] h-[80px] bg-gradient-to-b from-transparent via-[#007BFF]/30 to-transparent max-md:hidden"></div>
              <p className="text-[18px] text-[#555555] leading-[1.8] text-justify max-md:text-[16px] max-md:text-left">
                With a team of experienced specialists, we offer solutions that transform challenges into concrete opportunities for growth and expansion for our clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Separator Line 2 */}
      <div className="w-full h-[2px] bg-gradient-to-r from-transparent via-[#007BFF]/40 to-transparent"></div>

      {/* Services Section */}
      <section id="services" className="py-[120px] bg-[#F8F9FA] max-md:py-[80px]">
        <div className="max-w-[1200px] mx-auto px-[80px] max-md:px-[24px]">
          <div className="text-center mb-12 max-md:mb-10">
            <p className="text-[13px] font-medium text-[#007BFF] tracking-[2px] uppercase mb-5 max-md:text-[12px] max-md:mb-4">
              Our Services
            </p>
            <h2 className="text-[44px] font-semibold text-[#2C2C2C] font-serif max-md:text-[32px]">
              Specialized Solutions
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-10 mt-12 max-md:grid-cols-1 max-md:gap-6 max-md:mt-8">
            {/* Card 1 */}
            <div className="bg-white rounded-2xl p-12 shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.12)] hover:-translate-y-2 transition-all duration-300 cursor-pointer max-md:p-8">
              <div className="flex items-center justify-center w-20 h-20 rounded-full bg-[#E3F2FD] mx-auto mb-7 max-md:w-16 max-md:h-16 max-md:mb-5">
                <i className="ri-line-chart-line text-[48px] text-[#007BFF] max-md:text-[36px]"></i>
              </div>
              <h3 className="text-[22px] font-bold text-[#2C2C2C] text-center mb-4 max-md:text-[20px] max-md:mb-3">
                Sales Consulting
              </h3>
              <p className="text-[16px] text-[#666666] leading-[1.7] text-center max-md:text-[15px]">
                Customized strategies for optimizing commercial processes and market expansion
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-2xl p-12 shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.12)] hover:-translate-y-2 transition-all duration-300 cursor-pointer max-md:p-8">
              <div className="flex items-center justify-center w-20 h-20 rounded-full bg-[#E3F2FD] mx-auto mb-7 max-md:w-16 max-md:h-16 max-md:mb-5">
                <i className="ri-hand-heart-line text-[48px] text-[#007BFF] max-md:text-[36px]"></i>
              </div>
              <h3 className="text-[22px] font-bold text-[#2C2C2C] text-center mb-4 max-md:text-[20px] max-md:mb-3">
                Mergers & Acquisitions
              </h3>
              <p className="text-[16px] text-[#666666] leading-[1.7] text-center max-md:text-[15px]">
                Complete M&A advisory, from due diligence to post-transaction integration
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-2xl p-12 shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.12)] hover:-translate-y-2 transition-all duration-300 cursor-pointer max-md:p-8">
              <div className="flex items-center justify-center w-20 h-20 rounded-full bg-[#E3F2FD] mx-auto mb-7 max-md:w-16 max-md:h-16 max-md:mb-5">
                <i className="ri-search-line text-[48px] text-[#007BFF] max-md:text-[36px]"></i>
              </div>
              <h3 className="text-[22px] font-bold text-[#2C2C2C] text-center mb-4 max-md:text-[20px] max-md:mb-3">
                Market Analysis
              </h3>
              <p className="text-[16px] text-[#666666] leading-[1.7] text-center max-md:text-[15px]">
                Competitive intelligence and in-depth studies for well-founded strategic decisions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Separator Line 3 */}
      <div className="w-full h-[2px] bg-gradient-to-r from-transparent via-[#007BFF]/40 to-transparent"></div>

      {/* Team/Values Section */}
      <section className="py-[140px] bg-white relative overflow-hidden max-md:py-[80px]">
        {/* Decorative Elements */}
        <div className="absolute top-[20%] right-0 w-[300px] h-[1px] bg-gradient-to-l from-[#007BFF]/15 to-transparent"></div>
        <div className="absolute bottom-[40%] left-0 w-[250px] h-[1px] bg-gradient-to-r from-[#007BFF]/15 to-transparent"></div>
        
        <div className="max-w-[1200px] mx-auto px-[80px] relative z-10 max-md:px-[24px]">
          <div className="flex gap-[80px] items-center max-md:flex-col max-md:gap-10">
            {/* Left Side - Image */}
            <div className="w-[40%] max-md:w-full">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20business%20team%20meeting%20in%20modern%20corporate%20office%2C%20diverse%20group%20of%20executives%20discussing%20strategy%2C%20high%20quality%20corporate%20photography%2C%20confident%20professionals%20in%20business%20attire%2C%20collaborative%20work%20environment%20with%20natural%20lighting%20and%20glass%20walls&width=600&height=800&seq=team-cdg&orientation=portrait"
                alt="CDG Group Team"
                className="w-full h-full object-cover rounded-3xl max-md:h-[400px]"
              />
            </div>

            {/* Right Side - Content */}
            <div className="w-[60%] max-md:w-full">
              <p className="text-[13px] font-medium text-[#007BFF] tracking-[2px] uppercase mb-5 max-md:text-[12px] max-md:mb-4">
                Our Essence
              </p>
              <h2 className="text-[42px] font-semibold text-[#2C2C2C] font-serif mb-8 max-w-[80%] max-md:text-[32px] max-md:mb-6 max-md:max-w-full">
                Team of Specialists
              </h2>
              <p className="text-[18px] text-[#555555] leading-[1.8] mb-10 max-md:text-[16px] max-md:mb-8">
                Our team brings together professionals with global experience in strategic consulting, corporate finance and business management. We combine technical expertise with market vision to deliver exceptional results.
              </p>

              <div className="space-y-6 max-md:space-y-5">
                <div className="flex items-center gap-4 max-md:gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line text-[20px] text-[#007BFF] max-md:text-[18px]"></i>
                  </div>
                  <p className="text-[17px] font-medium text-[#2C2C2C] max-md:text-[16px]">
                    Excellence and Integrity
                  </p>
                </div>
                <div className="flex items-center gap-4 max-md:gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line text-[20px] text-[#007BFF] max-md:text-[18px]"></i>
                  </div>
                  <p className="text-[17px] font-medium text-[#2C2C2C] max-md:text-[16px]">
                    Customized Solutions
                  </p>
                </div>
                <div className="flex items-center gap-4 max-md:gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line text-[20px] text-[#007BFF] max-md:text-[18px]"></i>
                  </div>
                  <p className="text-[17px] font-medium text-[#2C2C2C] max-md:text-[16px]">
                    Measurable Results
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Separator Line 4 */}
      <div className="w-full h-[2px] bg-gradient-to-r from-transparent via-[#007BFF]/40 to-transparent"></div>

      {/* CTA Section */}
      <section className="py-[100px] bg-[#001F3F] max-md:py-[70px]">
        <div className="max-w-[1200px] mx-auto px-[80px] text-center max-md:px-[24px]">
          <h2 className="text-[52px] font-semibold text-white font-serif mb-6 drop-shadow-lg max-md:text-[36px] max-md:mb-5">
            Ready to Transform Your Business?
          </h2>
          <p className="text-[20px] text-white/80 mb-10 max-md:text-[18px] max-md:mb-8">
            Get in touch and discover how we can help
          </p>
          
          {!showEmail ? (
            <button
              onClick={() => setShowEmail(true)}
              className="px-20 py-4 bg-white text-[#001F3F] text-[17px] font-medium rounded-full hover:bg-[#E3F2FD] transition-all duration-300 whitespace-nowrap cursor-pointer max-md:px-12 max-md:py-3 max-md:text-[16px]"
            >
              Contact Us
            </button>
          ) : (
            <div className="inline-flex items-center gap-4 px-10 py-4 bg-white rounded-full animate-fade-in max-md:px-6 max-md:py-3 max-md:flex-col max-md:gap-2">
              <div className="w-6 h-6 flex items-center justify-center max-md:w-5 max-md:h-5">
                <i className="ri-mail-line text-[24px] text-[#001F3F] max-md:text-[20px]"></i>
              </div>
              <a 
                href="mailto:contact@cdggroup.site" 
                className="text-[17px] font-medium text-[#001F3F] hover:text-[#007BFF] transition-colors cursor-pointer max-md:text-[15px]"
              >
                contact@cdggroup.site
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Separator Line 5 */}
      <div className="w-full h-[2px] bg-gradient-to-r from-transparent via-[#007BFF]/40 to-transparent"></div>

      {/* Footer/Contact Section */}
      <footer id="contact" className="py-20 bg-[#1A1A1A] max-md:py-12">
        <div className="max-w-[1200px] mx-auto px-[80px] max-md:px-[24px]">
          <div className="grid grid-cols-2 gap-8 mb-20 max-md:grid-cols-1 max-md:gap-10 max-md:mb-12">
            {/* Left Column */}
            <div>
              <img
                src="https://static.readdy.ai/image/252f02ba3944c7db703aee568cd82e59/bb693eadee0c4a5cb50492cf8fb740f5.jpeg"
                alt="CDG Group"
                className="h-12 w-auto mb-5 max-md:h-10 max-md:mb-4"
              />
              <p className="text-[14px] text-[#AAAAAA] leading-[1.6] max-md:text-[13px]">
                Expertise in Business Consulting and M&A
              </p>
            </div>

            {/* Right Column */}
            <div>
              <h3 className="text-[18px] font-bold text-white mb-5 max-md:text-[17px] max-md:mb-4">Contact</h3>
              <div className="space-y-4 max-md:space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-[18px] h-[18px] flex items-center justify-center">
                    <i className="ri-mail-line text-[18px] text-[#AAAAAA] max-md:text-[16px]"></i>
                  </div>
                  <a href="mailto:contact@cdggroup.site" className="text-[15px] text-[#AAAAAA] hover:text-white transition-colors cursor-pointer max-md:text-[14px]">
                    contact@cdggroup.site
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-[#333333] pt-6 max-md:pt-5">
            <div className="flex justify-between items-center max-md:flex-col max-md:gap-4 max-md:text-center">
              <p className="text-[13px] text-[#777777] max-md:text-[12px]">
                © 2025 CDG Group. All rights reserved.
              </p>
              <div className="flex gap-6 max-md:gap-4">
                <a href="#" className="text-[13px] text-[#777777] hover:underline cursor-pointer max-md:text-[12px]">
                  Privacy Policy
                </a>
                <span className="text-[#777777]">|</span>
                <a href="#" className="text-[13px] text-[#777777] hover:underline cursor-pointer max-md:text-[12px]">
                  Terms of Use
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}